/**
 * Image & Content Block
 *
 * Draws the heading's underline bars on, left to right, as they scroll into
 * view. The bar is a ::before, which cannot carry a data-aos of its own, so
 * this watches the spans directly rather than going through AOS — the fade-ups
 * elsewhere in the block are still AOS's.
 *
 * The sweep itself is a keyframe in image-content.scss; all this does is add
 * the class that starts it and set the delay that spaces them out.
 */

const DRAWN_CLASS = "is-drawn";

// Held back after the bar comes into view, so the heading has landed and been
// read before anything is marked on it.
const START_DELAY_MS = 250;

// Enough for the eye to read several bars as a sequence rather than one sweep.
const STAGGER_MS = 150;

// Most of the word is on screen before its bar starts, so the underline reads
// as marking text the visitor is already looking at.
const VISIBLE_RATIO = 0.6;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function initImageContent(scope = document) {
  scope.querySelectorAll(".image-content__heading").forEach((heading) => {
    if (heading.dataset.underlinesInitialised) return;
    heading.dataset.underlinesInitialised = "true";

    const underlines = Array.from(
      heading.querySelectorAll(".image-content__underline"),
    );

    if (!underlines.length) return;

    // No observer to watch with, or the visitor has asked for less
    // movement. The bars are part of the heading, not decoration on top of
    // it, so they land drawn rather than never arriving — the
    // reduced-motion rule in the SCSS drops the sweep itself.
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      underlines.forEach((underline) => underline.classList.add(DRAWN_CLASS));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        // Stagger only what arrives together. Bars that scroll in one
        // at a time are already spaced out by the scrolling, so giving
        // them a delay too would just make them look late.
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry, index) => {
            entry.target.style.setProperty(
              "--underline-delay",
              `${START_DELAY_MS + index * STAGGER_MS}ms`,
            );
            entry.target.classList.add(DRAWN_CLASS);
            // Drawn once — it never needs watching again.
            obs.unobserve(entry.target);
          });
      },
      { threshold: VISIBLE_RATIO },
    );

    underlines.forEach((underline) => observer.observe(underline));
  });
}

document.addEventListener("DOMContentLoaded", () => initImageContent());

// Blocks are re-rendered as they are edited, so run again on each preview
// render (dispatched by resources/js/editor-block-init.js).
document.addEventListener("blockEditorInit", (e) => {
  if (e.detail && e.detail.block) {
    initImageContent(e.detail.block);
  }
});
